/**
 * Mark-read hook — 1 s debounced POST to `/api/v1/conversations/:id/read`.
 *
 *   const { markUpTo, isPending, error } = useMarkRead(conversationId)
 *   useEffect(() => {
 *     if (messages.length === 0) return
 *     markUpTo(messages[messages.length - 1].createdAt)
 *   }, [messages.length, ...])
 *
 * Pipeline:
 *   1. Caller invokes `markUpTo(atIso)` whenever the visible thread
 *      changes (e.g. scroll-to-bottom, opening the conversation).
 *   2. The call is debounced 1 s — chatty UIs (auto-scroll on every
 *      `message:new`) collapse into one POST.
 *   3. Optimistic: `applyReadUpdate(convId, myId, atIso)` advances the
 *      participant row immediately so the unread badge clears.
 *   4. REST returns the **effective** `lastReadAt` (server-side monotonic
 *      + future-clamp). Re-apply if it differs.
 *   5. Failures are silent — read receipts are non-critical, the next
 *      `markUpTo` retries automatically.
 *
 * Read receipts are non-essential — we deliberately do not surface
 * failures to the user. `error` is exposed for diagnostics only.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { readOnConversation } from '../services/messenger.socket'
import { useMessengerStore } from '../store/messenger.store'

const DEBOUNCE_MS = 1_000

interface UseMarkRead {
  markUpTo: (atIso: string) => void
  isPending: boolean
  error: string | null
}

export function useMarkRead(conversationId: string | null | undefined): UseMarkRead {
  const me = useAuthStore((s) => s.user)
  // My participant row (read cursor) is keyed by contactPresenceId; the socket
  // read:update push authenticates as the auth user (`me.id`).
  const presenceId = useAuthStore((s) => s.presenceId)
  const applyReadUpdate = useMessengerStore((s) => s.applyReadUpdate)

  const [isPending, setIsPending] = useState<boolean>(false)
  const [error, setError]         = useState<string | null>(null)

  // Track the last atIso we've asked the server to store so a flurry of
  // `markUpTo(sameTimestamp)` collapses to nothing.
  const lastFlushedRef = useRef<string | null>(null)
  const pendingIsoRef  = useRef<string | null>(null)
  const timerRef       = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reset when the conversation changes.
  useEffect(() => {
    lastFlushedRef.current = null
    pendingIsoRef.current = null
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [conversationId])

  // Clean up any pending debounce on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  const markUpTo = useCallback(
    (atIso: string): void => {
      if (!conversationId || !me?.id || !presenceId) return
      if (!atIso) return
      // Already flushed this exact timestamp? Skip.
      if (lastFlushedRef.current === atIso) return
      pendingIsoRef.current = atIso

      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(async () => {
        timerRef.current = null
        const toFlush = pendingIsoRef.current
        if (!toFlush) return
        if (lastFlushedRef.current === toFlush) return

        // Optimistic — advance the participant's lastReadAt right away.
        applyReadUpdate(conversationId, presenceId, toFlush)

        setIsPending(true)
        setError(null)
        try {
          // Signal-driven: push read:update over the conversation channel; the
          // server fans out read:updated (sender ✓✓ blue) + persists the cursor.
          await readOnConversation(me.id, conversationId, toFlush, me.name)
          lastFlushedRef.current = toFlush
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'mark read failed'
          setError(message)
          // Don't bump lastFlushedRef — the next attempt should retry.
        } finally {
          setIsPending(false)
        }
      }, DEBOUNCE_MS)
    },
    [conversationId, me?.id, me?.name, presenceId, applyReadUpdate],
  )

  return { markUpTo, isPending, error }
}
